import React, { useEffect, useState, useContext } from "react";
import { Container, Row, Col } from "react-bootstrap";
import AdminLayout from "../../../../layouts/admin/AdminLayout";
import AppContext from "../../../../context/AppContext";
import "../../../../assets/css/admin/dashboardcss/AdminBlogs.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { checkLoggedInAdmin } from "../../../../helper/CustomHelper";
import Swal from "sweetalert2";

export default function AdminBlogs() {
  const { baseUrl, baseUrlForFile } = useContext(AppContext);
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [body, setBody] = useState("");
  const [image, setImage] = useState(null);

  const [posts, setPosts] = useState([]);
  const [view, setView] = useState("list");
  const [activePost, setActivePost] = useState(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editPost, setEditPost] = useState(null);

  const [isOpeningAdd, setIsOpeningAdd] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [errors, setErrors] = useState({});

  const resetErrors = () => {
    setErrors({});
  };

  const resetForm = () => {
    setTitle("");
    setExcerpt("");
    setBody("");
    setImage(null);
    setEditPost(null);
    resetErrors();
  };

  const isFormValid = (requireImage) => {
    const newErrors = {};

    if (!title.trim()) {
      newErrors.title = "Title is required.";
    }

    if (!excerpt.trim()) {
      newErrors.excerpt = "Excerpt is required.";
    }

    if (!body.trim()) {
      newErrors.body = "Body is required.";
    }

    if (requireImage && !image) {
      newErrors.image = "Image is required.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const viewAllBlogs = () => {
    axios
      .get(`${baseUrl}/admin/blog/viewAllBlogs`)
      .then((response) => {
        setPosts(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        console.log(error);
        setPosts([]);
      });
  };

  const createBlog = () => {
    if (!isFormValid(true)) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please fill in all required fields and select an image.",
        confirmButtonColor: "#E8879D",
      });
      return;
    }

    setIsSubmitting(true);

    const formData = new FormData();

    formData.append("title", title);
    formData.append("excerpt", excerpt);
    formData.append("body", body);
    formData.append("file", image);

    axios
      .post(`${baseUrl}/admin/blog/createBlog`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        console.log(response.data);

        viewAllBlogs();

        setShowAddModal(false);
        resetForm();

        Swal.fire({
          icon: "success",
          title: "Post Published!",
          text: "New blog post has been created successfully.",
          confirmButtonColor: "#E8879D",
        });
      })
      .catch((error) => {
        console.log(error);

        if (error.response?.status === 422) {
          setErrors(error.response.data.errors || {});
        }

        Swal.fire({
          icon: "error",
          title: "Save Failed!",
          text:
            error.response?.data?.message ||
            "Unable to create blog post. Please try again.",
          confirmButtonColor: "#E8879D",
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const updateBlog = () => {
    if (!isFormValid(false)) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please fill in all required fields.",
        confirmButtonColor: "#E8879D",
      });
      return;
    }

    if (!editPost) {
      return;
    }

    setIsSubmitting(true);

    const formData = new FormData();

    formData.append("id", editPost.id);
    formData.append("title", title);
    formData.append("excerpt", excerpt);
    formData.append("body", body);

    if (image) {
      formData.append("file", image);
    }

    axios
      .post(`${baseUrl}/admin/blog/updateBlog`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        console.log(response.data);

        viewAllBlogs();

        setShowEditModal(false);
        resetForm();

        Swal.fire({
          icon: "success",
          title: "Post Updated!",
          text: "Blog post has been updated successfully.",
          confirmButtonColor: "#E8879D",
        });
      })
      .catch((error) => {
        console.log(error);

        if (error.response?.status === 422) {
          setErrors(error.response.data.errors || {});
        }

        Swal.fire({
          icon: "error",
          title: "Update Failed!",
          text:
            error.response?.data?.message ||
            "Unable to update blog post. Please try again.",
          confirmButtonColor: "#E8879D",
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const deleteBlog = (id) => {
    Swal.fire({
      title: "Delete this post?",
      text: "This blog post will be permanently removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF7C7C",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      setDeletingId(id);

      axios
        .post(`${baseUrl}/admin/blog/deleteBlog`, {
          id: id,
        })
        .then((response) => {
          console.log(response.data);

          viewAllBlogs();

          if (activePost?.id === id) {
            setActivePost(null);
            setView("list");
          }

          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: "Blog post has been removed successfully.",
            confirmButtonColor: "#E8879D",
          });
        })
        .catch((error) => {
          console.log(error);

          Swal.fire({
            icon: "error",
            title: "Delete Failed!",
            text:
              error.response?.data?.message ||
              "Unable to delete blog post. Please try again.",
            confirmButtonColor: "#E8879D",
          });
        })
        .finally(() => {
          setDeletingId(null);
        });
    });
  };

  const handleViewDetail = (post) => {
    setActivePost(post);
    setView("detail");
  };

  const openAddModal = () => {
    setIsOpeningAdd(true);

    setTimeout(() => {
      resetForm();
      setShowAddModal(true);
      setIsOpeningAdd(false);
    }, 300);
  };

  const openEditModal = (post) => {
    setEditPost(post);
    setTitle(post.title || "");
    setExcerpt(post.excerpt || "");
    setBody(post.body || "");
    setImage(null);
    resetErrors();
    setShowEditModal(true);
  };

  useEffect(() => {
    checkLoggedInAdmin(navigate);
    viewAllBlogs();
  }, []);

  if (view === "detail" && activePost) {
    return (
      <AdminLayout>
        <div className="esl-blogs-wrapper">
          <div className="esl-detail-header">
            <button
              className="esl-back-btn"
              onClick={() => {
                setView("list");
                setActivePost(null);
              }}
            >
              Back to Journal
            </button>
          </div>

          <div className="esl-detail-card">
            <div className="esl-detail-image-wrap">
              <img
                src={`${baseUrlForFile}/${activePost.image}`}
                alt={activePost.title}
                className="esl-detail-image"
              />
            </div>

            <div className="esl-detail-body">
              <h1 className="esl-detail-title">
                {activePost.title}
              </h1>

              <p className="esl-detail-excerpt">
                {activePost.excerpt}
              </p>

              <div className="esl-detail-divider" />

              <p className="esl-detail-content">
                {activePost.body}
              </p>

              <div className="esl-detail-actions">
                <button
                  className="esl-btn-edit"
                  disabled={deletingId === activePost.id}
                  onClick={() => openEditModal(activePost)}
                >
                  Edit
                </button>

                <button
                  className="esl-btn-delete"
                  disabled={deletingId === activePost.id}
                  onClick={() => deleteBlog(activePost.id)}
                >
                  {deletingId === activePost.id
                    ? "Deleting..."
                    : "Delete"}
                </button>
              </div>
            </div>
          </div>

          {showEditModal && editPost && (
            <>
              <div
                className="modal show d-block esl-modal"
                tabIndex="-1"
              >
                <div className="modal-dialog modal-dialog-centered esl-modal-dialog">
                  <div className="modal-content esl-modal-content">

                    <div className="esl-modal-header">
                      <h5 className="esl-modal-title">
                        Edit Blog
                      </h5>

                      <button
                        className="esl-modal-close"
                        onClick={() => {
                          if (!isSubmitting) {
                            setShowEditModal(false);
                            resetForm();
                          }
                        }}
                        disabled={isSubmitting}
                      >
                        ×
                      </button>
                    </div>

                    <div className="esl-modal-body">

                      <div className="esl-form-group">
                        <label className="esl-form-label">
                          Title
                        </label>

                        <input
                          type="text"
                          className="esl-form-input"
                          value={title}
                          onChange={(e) =>
                            setTitle(e.target.value)
                          }
                          disabled={isSubmitting}
                        />

                        {errors.title && (
                          <small style={{ color: "red" }}>
                            {errors.title}
                          </small>
                        )}
                      </div>

                      <div className="esl-form-group">
                        <label className="esl-form-label">
                          Excerpt
                        </label>

                        <input
                          type="text"
                          className="esl-form-input"
                          value={excerpt}
                          onChange={(e) =>
                            setExcerpt(e.target.value)
                          }
                          disabled={isSubmitting}
                        />

                        {errors.excerpt && (
                          <small style={{ color: "red" }}>
                            {errors.excerpt}
                          </small>
                        )}
                      </div>

                      <div className="esl-form-group">
                        <label className="esl-form-label">
                          Body
                        </label>

                        <textarea
                          rows="5"
                          className="esl-form-textarea"
                          value={body}
                          onChange={(e) =>
                            setBody(e.target.value)
                          }
                          disabled={isSubmitting}
                        />

                        {errors.body && (
                          <small style={{ color: "red" }}>
                            {errors.body}
                          </small>
                        )}
                      </div>

                      <div className="esl-form-group">
                        <label className="esl-form-label">
                          Image
                        </label>

                        <input
                          type="file"
                          className="esl-form-input"
                          onChange={(e) =>
                            setImage(e.target.files[0])
                          }
                          disabled={isSubmitting}
                        />
                      </div>

                      <div className="esl-modal-footer-actions">
                        <button
                          className="esl-btn-save"
                          onClick={updateBlog}
                          disabled={isSubmitting}
                        >
                          {isSubmitting
                            ? "Updating..."
                            : "Update"}
                        </button>
                      </div>

                    </div>
                  </div>
                </div>
              </div>

              <div
                className="modal-backdrop fade show"
                onClick={() => {
                  if (!isSubmitting) {
                    setShowEditModal(false);
                    resetForm();
                  }
                }}
              />
            </>
          )}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="esl-blogs-wrapper">

        <div className="esl-blogs-header">
          <div>
            <h1 className="esl-blogs-title">
              Journal
            </h1>

            <p className="esl-blogs-subtitle">
              Write and edit blog posts.
            </p>
          </div>

          <button
            className="esl-new-post-btn"
            onClick={openAddModal}
            disabled={isOpeningAdd}
          >
            {isOpeningAdd ? "Opening..." : "+ New Post"}
          </button>
        </div>

        <Container fluid className="px-0">
          <Row className="g-4">

            {posts.map((post, index) => (
              <Col
                key={post.id}
                xs={12}
                sm={6}
                xl={4}
              >
                <div
                  className="esl-blog-card"
                  style={{
                    animationDelay: `${index * 0.07}s`,
                  }}
                  onClick={() =>
                    handleViewDetail(post)
                  }
                >

                  <div className="esl-blog-image-wrap">
                    <img
                      src={`${baseUrlForFile}/${post.image}`}
                      alt={post.title}
                      className="esl-blog-image"
                    />
                  </div>

                  <div className="esl-blog-body">

                    <h3 className="esl-blog-title">
                      {post.title}
                    </h3>

                    <p className="esl-blog-excerpt">
                      {post.excerpt}
                    </p>

                    <div className="esl-blog-actions">

                      <button
                        className="esl-btn-edit"
                        disabled={deletingId === post.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditModal(post);
                        }}
                      >
                        Edit
                      </button>

                      <button
                        className="esl-btn-delete"
                        disabled={deletingId === post.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteBlog(post.id);
                        }}
                      >
                        {deletingId === post.id
                          ? "Deleting..."
                          : "Delete"}
                      </button>

                    </div>
                  </div>
                </div>
              </Col>
            ))}

          </Row>
        </Container>

        {showAddModal && (
          <>
            <div
              className="modal show d-block esl-modal"
              tabIndex="-1"
            >
              <div className="modal-dialog modal-dialog-centered esl-modal-dialog">
                <div className="modal-content esl-modal-content">

                  <div className="esl-modal-header">
                    <h5 className="esl-modal-title">
                      New Blog
                    </h5>

                    <button
                      className="esl-modal-close"
                      onClick={() => {
                        if (!isSubmitting) {
                          setShowAddModal(false);
                          resetForm();
                        }
                      }}
                      disabled={isSubmitting}
                    >
                      ×
                    </button>
                  </div>

                  <div className="esl-modal-body">

                    <div className="esl-form-group">
                      <label className="esl-form-label">
                        Title
                      </label>

                      <input
                        type="text"
                        className="esl-form-input"
                        value={title}
                        onChange={(e) =>
                          setTitle(e.target.value)
                        }
                        disabled={isSubmitting}
                      />

                      {errors.title && (
                        <small style={{ color: "red" }}>
                          {errors.title}
                        </small>
                      )}
                    </div>

                    <div className="esl-form-group">
                      <label className="esl-form-label">
                        Excerpt
                      </label>

                      <input
                        type="text"
                        className="esl-form-input"
                        value={excerpt}
                        onChange={(e) =>
                          setExcerpt(e.target.value)
                        }
                        disabled={isSubmitting}
                      />

                      {errors.excerpt && (
                        <small style={{ color: "red" }}>
                          {errors.excerpt}
                        </small>
                      )}
                    </div>

                    <div className="esl-form-group">
                      <label className="esl-form-label">
                        Body
                      </label>

                      <textarea
                        rows="5"
                        className="esl-form-textarea"
                        value={body}
                        onChange={(e) =>
                          setBody(e.target.value)
                        }
                        disabled={isSubmitting}
                      />

                      {errors.body && (
                        <small style={{ color: "red" }}>
                          {errors.body}
                        </small>
                      )}
                    </div>

                    <div className="esl-form-group">
                      <label className="esl-form-label">
                        Image
                      </label>

                      <input
                        type="file"
                        className="esl-form-input"
                        onChange={(e) =>
                          setImage(e.target.files[0])
                        }
                        disabled={isSubmitting}
                      />

                      {errors.image && (
                        <small style={{ color: "red" }}>
                          {errors.image}
                        </small>
                      )}
                    </div>

                    <div className="esl-modal-footer-actions">
                      <button
                        className="esl-btn-save"
                        onClick={createBlog}
                        disabled={isSubmitting}
                      >
                        {isSubmitting
                          ? "Saving..."
                          : "Save"}
                      </button>
                    </div>

                  </div>
                </div>
              </div>
            </div>

            <div
              className="modal-backdrop fade show"
              onClick={() => {
                if (!isSubmitting) {
                  setShowAddModal(false);
                  resetForm();
                }
              }}
            />
          </>
        )}

        {showEditModal && editPost && (
          <>
            <div
              className="modal show d-block esl-modal"
              tabIndex="-1"
            >
              <div className="modal-dialog modal-dialog-centered esl-modal-dialog">
                <div className="modal-content esl-modal-content">

                  <div className="esl-modal-header">
                    <h5 className="esl-modal-title">
                      Edit Blog
                    </h5>

                    <button
                      className="esl-modal-close"
                      onClick={() => {
                        if (!isSubmitting) {
                          setShowEditModal(false);
                          resetForm();
                        }
                      }}
                      disabled={isSubmitting}
                    >
                      ×
                    </button>
                  </div>

                  <div className="esl-modal-body">

                    <div className="esl-form-group">
                      <label className="esl-form-label">
                        Title
                      </label>

                      <input
                        type="text"
                        className="esl-form-input"
                        value={title}
                        onChange={(e) =>
                          setTitle(e.target.value)
                        }
                        disabled={isSubmitting}
                      />

                      {errors.title && (
                        <small style={{ color: "red" }}>
                          {errors.title}
                        </small>
                      )}
                    </div>

                    <div className="esl-form-group">
                      <label className="esl-form-label">
                        Excerpt
                      </label>

                      <input
                        type="text"
                        className="esl-form-input"
                        value={excerpt}
                        onChange={(e) =>
                          setExcerpt(e.target.value)
                        }
                        disabled={isSubmitting}
                      />

                      {errors.excerpt && (
                        <small style={{ color: "red" }}>
                          {errors.excerpt}
                        </small>
                      )}
                    </div>

                    <div className="esl-form-group">
                      <label className="esl-form-label">
                        Body
                      </label>

                      <textarea
                        rows="5"
                        className="esl-form-textarea"
                        value={body}
                        onChange={(e) =>
                          setBody(e.target.value)
                        }
                        disabled={isSubmitting}
                      />

                      {errors.body && (
                        <small style={{ color: "red" }}>
                          {errors.body}
                        </small>
                      )}
                    </div>

                    <div className="esl-form-group">
                      <label className="esl-form-label">
                        Image
                      </label>

                      <input
                        type="file"
                        className="esl-form-input"
                        onChange={(e) =>
                          setImage(e.target.files[0])
                        }
                        disabled={isSubmitting}
                      />
                    </div>

                    <div className="esl-modal-footer-actions">
                      <button
                        className="esl-btn-save"
                        onClick={updateBlog}
                        disabled={isSubmitting}
                      >
                        {isSubmitting
                          ? "Updating..."
                          : "Update"}
                      </button>
                    </div>

                  </div>
                </div>
              </div>
            </div>

            <div
              className="modal-backdrop fade show"
              onClick={() => {
                if (!isSubmitting) {
                  setShowEditModal(false);
                  resetForm();
                }
              }}
            />
          </>
        )}

      </div>
    </AdminLayout>
  );
}